import {
  ChannelType,
  ISalePageDetailV2Response,
  ISalePageSellerInfoResponse,
} from '@/api/types';
import { isNonNil } from '@vuddy/utils';

// 사업자 상세 필드는 swagger에서 제거되었으나, 응답에 우연히 포함되더라도
// 노출할 수 있도록 옵셔널 가드 형태로 타입을 확장해둔다.
type SellerInfoWithLegacyDetails = ISalePageSellerInfoResponse & {
  bizNumber?: string;
  businessName?: string;
  representativeName?: string;
  businessAddress?: string;
};

/** HIDDEN: 섹션 전체 미표시 / FULL: 전체 정보 표시 */
type DisplayMode = 'HIDDEN' | 'FULL';

/** 채널 타입을 사용자 노출용 한국어 라벨로 변환한다. */
function getBizTypeLabel(channelType: ChannelType): string {
  switch (channelType) {
    case 'CREATOR':
      return '크리에이터';
    case 'SECONDARY_CREATOR':
      return '2차 창작자';
    default:
      return '';
  }
}

/**
 * 판매 정보 섹션의 표시 모드를 결정한다.
 *
 * - salePage.type이 없으면 HIDDEN (판매 페이지 타입 미확정)
 * - 그 외 모든 경우 FULL
 *
 * BASIC_ONLY 분기는 이 함수 밖(SellerInfoSection)에서 처리한다.
 */
export function getDisplayMode(
  salePage?: Partial<ISalePageDetailV2Response>
): DisplayMode {
  if (!salePage?.type) return 'HIDDEN';

  return 'FULL';
}

/**
 * 판매자 기본 정보 항목 배열을 반환한다.
 *
 * 사업자 유형 판별 기준 (API 응답 필드 존재 여부):
 *   - 개인        : bizNumber 없음
 *   - 개인사업자  : bizNumber 있음, businessName 없음
 *   - 법인사업자  : bizNumber 있음, businessName 있음
 *
 * 항목 구조:
 *   [판매자 유형, 판매 주체]
 *   + 2차 창작자이면 [원작 크리에이터 채널]
 *   + 사업자 또는 2차창작자이면 빈줄 후 상세 항목
 *     (법인사업자이면 법인명 → 대표자명 → 사업자번호 → 영업소재지)
 *
 * 빈 문자열('')은 UI에서 항목 간 구분선(빈 줄)으로 렌더링된다.
 */
// TODO: 임시 플래그 - false로 변경하거나 제거하면 전체 정보(사업자 상세) 복원
export const TEMP_HIDE_DETAIL_INFO = true;

export function getBasicInfoItems(
  sellerInfo: SellerInfoWithLegacyDetails
): string[] {
  const items: string[] = [];

  // 사업자 유형 플래그
  const isSecondary = sellerInfo.channelType === 'SECONDARY_CREATOR'; // 2차 창작자 채널 여부
  const isBusiness = isNonNil(sellerInfo.bizNumber); // 사업자 여부 (개인사업자 또는 법인사업자)
  const isCorporate = isBusiness && isNonNil(sellerInfo.businessName); // 법인사업자 여부 (bizNumber + businessName 모두 존재)

  // 공통 항목: 판매자 유형 + 판매 주체
  const bizLabel = getBizTypeLabel(sellerInfo.channelType);
  if (bizLabel) items.push(`판매자 유형 : ${bizLabel}`);
  items.push(`판매 주체 : ${sellerInfo.sellerSubjectName}`);

  // 2차 창작자 전용: 원작 크리에이터 채널
  if (isSecondary && isNonNil(sellerInfo.originalCreatorChannelName)) {
    items.push(
      `원작 크리에이터 채널 : ${sellerInfo.originalCreatorChannelName}`
    );
  }

  // 크리에이터 개인: 공통 항목만 반환
  if (!isSecondary && !isBusiness) return items;

  if (TEMP_HIDE_DETAIL_INFO) return items;

  // 사업자 또는 2차창작자: 빈줄 + 상세 항목
  const detailItems: string[] = [];

  // 법인사업자만 법인명 표시
  if (isCorporate) {
    detailItems.push(`법인명 : ${sellerInfo.businessName}`);
  }

  if (isNonNil(sellerInfo.representativeName)) {
    detailItems.push(`대표자명 : ${sellerInfo.representativeName}`);
  }

  // 사업자번호·영업소재지는 사업자(개인/법인) 공통
  if (isBusiness) {
    detailItems.push(`사업자번호 : ${sellerInfo.bizNumber}`);
    if (isNonNil(sellerInfo.businessAddress)) {
      detailItems.push(`영업소재지 : ${sellerInfo.businessAddress}`);
    }
  }

  if (detailItems.length > 0) {
    items.push('', ...detailItems); // 빈 문자열로 시각적 구분
  }

  return items;
}

/**
 * 판매자 운영 정보(연락처)를 반환한다.
 *
 * PHYSICAL + CREATOR_GOODS인 경우에만 표시한다.
 * CREATOR_GOODS이면 deliveryType은 항상 CREATOR이므로 배송 타입 분기는 불필요하다.
 * DIGITAL, FUNDING, CLASS, GACHA 등 그 외 판매 타입은 운영 정보를 표시하지 않는다.
 */
export function getOperationInfoItems(
  sellerInfo: ISalePageSellerInfoResponse,
  salePage?: Partial<ISalePageDetailV2Response>
): string[] {
  if (TEMP_HIDE_DETAIL_INFO) return [];

  const isCreatorGoods =
    salePage?.type === 'PHYSICAL' &&
    salePage.physical?.detailType === 'CREATOR_GOODS';

  if (!isCreatorGoods) return [];

  return [`연락처 : ${sellerInfo.contact}`];
}
